import React from 'react';
import { Box, Typography, LinearProgress, Alert } from '@mui/material';

/**
 * Displays Performance Assessment with recommendation, confidence level, and risk assessment
 * Monochrome black and white theme
 */
const RiskAssessmentAlert = ({ recommendation = 'PENDING', confidenceLevel = 'MEDIUM', overallScore = 0, riskAssessment }) => {
  if (!riskAssessment) return null;

  // Calculate risk level based on keywords
  const calculateRiskLevel = (text) => {
    const lowerText = text.toLowerCase();
    const highRiskKeywords = ['high risk', 'critical', 'severe', 'major concerns', 'unsuitable'];
    const mediumRiskKeywords = ['moderate risk', 'some concerns', 'potential issues', 'caution'];

    if (highRiskKeywords.some((keyword) => lowerText.includes(keyword))) {
      return { level: 80, label: 'HIGH', color: 'rgba(255, 255, 255, 0.95)' };
    }
    if (mediumRiskKeywords.some((keyword) => lowerText.includes(keyword))) {
      return { level: 50, label: 'MEDIUM', color: 'rgba(255, 255, 255, 0.9)' };
    }
    return { level: 20, label: 'LOW', color: 'rgba(255, 255, 255, 0.85)' };
  };

  const risk = calculateRiskLevel(riskAssessment);

  // Get decision display based on recommendation
  const getDecisionDisplay = () => {
    if (recommendation === 'HIRE') return { text: 'ACCEPT', color: 'rgba(255, 255, 255, 0.95)' };
    if (recommendation === 'MAYBE') return { text: 'REVIEW', color: 'rgba(255, 255, 255, 0.9)' };
    return { text: 'REJECT', color: 'rgba(255, 255, 255, 0.85)' };
  };

  const decision = getDecisionDisplay();

  return (
    <Box
      sx={{
        position: 'relative',
        p: 2.5,
        mb: 2.5,
        backgroundColor: 'rgba(12, 14, 22, 0.82)',
        borderRadius: '20px',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 14px 36px rgba(0,0,0,0.35)',
        backdropFilter: 'blur(14px)',
        overflow: 'visible',
        transition: 'all 0.25s ease',
        '&:hover': {
          boxShadow: '0 18px 42px rgba(0,0,0,0.45)',
          borderColor: 'rgba(255,255,255,0.18)',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header - Single Line Layout */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.95)',
              fontWeight: 600,
              fontSize: '1.05rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Performance Assessment
          </Typography>
          
          {/* Single Line Decision Bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '12px',
              px: 2.5,
              py: 1.2,
              border: '1px solid rgba(255,255,255,0.08)',
              flexWrap: 'wrap',
            }}
          >
            {/* Decision */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  color: decision.color,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  letterSpacing: '0.02em',
                }}
              >
                {decision.text}
              </Typography>
            </Box>

            {/* Divider */}
            <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(255,255,255,0.15)' }} />

            {/* Confidence Level */}
            <Box>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              >
                {confidenceLevel} Confidence
              </Typography>
            </Box>

            {/* Divider */}
            <Box sx={{ width: '1px', height: '20px', backgroundColor: 'rgba(255,255,255,0.15)' }} />

            {/* Overall Score */}
            <Box>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              >
                Score: {overallScore}/100
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Risk Description */}
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: 'rgba(255,255,255,0.03)',
            borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, letterSpacing: '0.01em' }}>
            {riskAssessment}
          </Typography>
        </Box>

        {/* Risk Level Indicator */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Risk Level
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: risk.color,
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            >
              {risk.label} ({risk.level}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={risk.level}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {/* Recommendation */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'rgba(255,255,255,0.03)',
            borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', lineHeight: 1.5 }}>
            {risk.label === 'HIGH'
              ? 'Recommendation: Exercise extreme caution. Consider additional screening or reject.'
              : risk.label === 'MEDIUM'
              ? 'Recommendation: Review carefully. May require additional interviews or references.'
              : 'Recommendation: Acceptable risk level. Proceed with standard hiring process.'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RiskAssessmentAlert;
